import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';

const AdCarousel = ({ areaId = 1, className = '', onAdClick = () => { } }) => {
  const [ads, setAds] = useState([]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchAds();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [areaId]);

  useEffect(() => {
    if (ads.length > 0) {
      startCarousel();
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [ads]); // currentIndex dependency removed to prevent loop

  const fetchAds = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/advertisements/area/${areaId}`, {
        cache: 'no-cache'
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const fetchedAds = Array.isArray(data.ads) ? data.ads : [];
      const apiEnabled = data.enabled !== undefined ? data.enabled : true;

      // Filter out ads that have passed their end date
      const validAds = fetchedAds.filter(ad => {
        const now = new Date();
        const startDate = new Date(ad.startDate);
        const endDate = ad.endDate ? new Date(ad.endDate) : null;

        return ad.active && startDate <= now && (!endDate || endDate >= now);
      });

      setAds(validAds);
      setIsEnabled(apiEnabled);
      setCurrentIndex(0);

    } catch (error) {
      console.error('Error fetching ads:', error);
      setError('Reklamlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const startCarousel = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (ads.length <= 1) return;

    // Use a function to get current index and set next, avoiding dependency
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % ads.length;
        return nextIndex;
      });
    }, 5000); // Fixed duration to prevent complexity
  };

  const handleAdClick = (ad) => {
    if (ad.content?.buttonLink) {
      window.open(ad.content.buttonLink, '_blank');
    }
    onAdClick(ad);
  };

  if (loading) {
    return (
      <div className={`${className} animate-pulse`}>
        <div className={'relative flex flex-col overflow-hidden rounded-3xl bg-gray-200 shadow-md md:flex-row border border-neutral-100 h-64'}>
          <div className={'h-48 w-full md:h-auto md:w-2/5 bg-gray-300'}></div>
          <div className={'flex flex-1 flex-col justify-center p-8'}>
            <div className={'h-4 bg-gray-300 rounded mb-2 w-1/3'}></div>
            <div className={'h-6 bg-gray-300 rounded mb-3 w-2/3'}></div>
            <div className={'h-4 bg-gray-300 rounded mb-6 w-full'}></div>
            <div className={'h-10 bg-gray-300 rounded w-32'}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isEnabled) {
    return null;
  }

  if (error || ads.length === 0) {
    return (
      <div className={`${className}`}>
        <div className={'relative flex flex-col overflow-hidden rounded-3xl bg-white dark:bg-[#09090b] shadow-md transition-shadow hover:shadow-lg md:flex-row border border-neutral-100 dark:border-white/5'}>
          <div className={'h-48 w-full md:h-auto md:w-2/5 bg-cover bg-center'} style={{ backgroundImage: `url('https://cdn.pirge.com/images/product/1k75j-350652.jpg')` }}>
            <div className={'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/10'} />
          </div>
          <div className={'flex flex-1 flex-col justify-center p-8'}>
            <div className={'flex items-center gap-2 mb-2'}>
              <span className={'inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-bold text-yellow-800'}>YENİ ÜRÜN</span>
              <span className={'text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'}>Mutfak Gereçleri</span>
            </div>
            <h3 className={'text-2xl font-bold text-gray-900 dark:text-white mb-3'}>Şefin Özel Bıçak Seti</h3>
            <p className={'text-gray-500 dark:text-gray-400 mb-6 max-w-lg'}>Profesyonel mutfak deneyimini evinize taşıyın. Paslanmaz çelik, ergonomik saplı bıçak setimizde lansmana özel %20 indirim fırsatı.</p>
            <div className={'flex gap-4'}>
              <button className={'w-fit rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'}>Şimdi Keşfet</button>
              <button className={'w-fit rounded-xl border border-neutral-200 px-6 py-3 text-sm font-semibold text-gray-900 dark:text-white transition-transform hover:bg-neutral-50 hover:scale-105'}>Daha Sonra</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentAd = ads[currentIndex];

  return (
    <div className={`${className} relative group`}>
      <div className={'relative flex flex-col overflow-hidden rounded-3xl bg-white dark:bg-[#09090b] shadow-md transition-shadow hover:shadow-lg md:flex-row border border-neutral-100 dark:border-white/5'}>
        {/* Image Section */}
        {currentAd.content?.image && (
          <div className={'h-48 w-full md:h-auto md:w-2/5 bg-cover bg-center relative'}
            style={{ backgroundImage: `url('${currentAd.content.image}')` }}>
            <div className={'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/10'} />
          </div>
        )}

        {/* Content Section */}
        <div className={'flex flex-1 flex-col justify-center p-8'}>
          {/* Subtitle */}
          {currentAd.content?.subtitle && (
            <div className={'flex items-center gap-2 mb-2'}>
              <span className={'inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-bold text-yellow-800'}>
                {currentAd.content.subtitle}
              </span>
            </div>
          )}

          {/* Title */}
          <h3 className={'text-2xl font-bold text-gray-900 dark:text-white mb-3'}>
            {currentAd.content?.title || currentAd.title}
          </h3>

          {/* Description */}
          {currentAd.content?.description && (
            <p className={'text-gray-500 dark:text-gray-400 mb-6 max-w-lg'}>
              {currentAd.content.description}
            </p>
          )}

          {/* Buttons */}
          <div className={'flex gap-4'}>
            {currentAd.content?.buttonText && (
              <button
                onClick={() => handleAdClick(currentAd)}
                className={'w-fit rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'}
              >
                {currentAd.content.buttonText}
              </button>
            )}
          </div>
        </div>

        {/* Carousel Controls (Hidden per user request) */}
      </div>
    </div>
  );
};

export default AdCarousel;